import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sunrise, AlertTriangle, CheckCircle2, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BG_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/b57813e0-5c3f-47ce-8b83-46eb70572e41/images/d589b8d282d757d613c71187065b1d1426789ff79b663ae8439203cc8c671a78.png';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not reset your password. Please request a new link.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative"
      style={{ backgroundImage: `url(${BG_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
      <div className="absolute inset-0 bg-slate-900/60" />
      <Card className="w-full max-w-md shadow-2xl border-0 relative z-10 bg-white/95 backdrop-blur-sm" data-testid="reset-password-card">
        <CardHeader className="space-y-3">
          <div className="flex flex-col items-center justify-center mb-2">
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-4 rounded-lg mb-3 shadow-lg">
              <Sunrise className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">EQUIPR Horizon</h1>
          </div>
          <CardTitle className="text-xl font-semibold text-center tracking-tight text-slate-700">Set a new password</CardTitle>
        </CardHeader>
        <CardContent>
          {!token ? (
            <div className="text-center space-y-4" data-testid="reset-no-token">
              <p className="text-sm text-red-600">This reset link is missing its token. Please use the link from your email or request a new one.</p>
              <Button type="button" onClick={() => navigate('/login')} className="w-full bg-amber-500 hover:bg-amber-600 text-white h-10">Back to Sign In</Button>
            </div>
          ) : done ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
              <p className="text-sm text-slate-600">Your password has been reset. You can now sign in with your new password.</p>
              <Button type="button" onClick={() => navigate('/login')} data-testid="reset-goto-login"
                className="w-full bg-amber-500 hover:bg-amber-600 text-white h-10 font-medium">Go to Sign In</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700" data-testid="reset-error">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="new_password" className="text-sm font-semibold text-slate-700">New Password</Label>
                <div className="relative">
                  <Input id="new_password" type={showPw ? 'text' : 'password'} data-testid="new-password-input" value={password}
                    onChange={(e) => setPassword(e.target.value)} required className="h-10 border-slate-300 pr-10"
                    placeholder="At least 8 chars, 1 upper, 1 lower, 1 number" />
                  <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password" className="text-sm font-semibold text-slate-700">Confirm Password</Label>
                <Input id="confirm_password" type={showPw ? 'text' : 'password'} data-testid="confirm-password-input" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} required className="h-10 border-slate-300" placeholder="Re-enter new password" />
              </div>
              <Button type="submit" data-testid="reset-submit-button" disabled={submitting}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white shadow-md rounded-md h-10 font-medium transition-colors">
                {submitting ? 'Resetting…' : 'Reset Password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
